import { useCallback, useRef, useState } from "react";

/** Measures an element's own rendered height (ResizeObserver-based) so a
 * sibling spacer can reserve that much room when the element itself is
 * `position: fixed`. Uses a callback ref (not useRef+useEffect) because
 * the element is often conditionally rendered after data loads — a plain
 * useEffect with an empty deps array would run once against a null ref
 * and never re-attach once the element actually appears. */
export function useSelfHeight() {
  const [height, setHeight] = useState(0);
  const cleanupRef = useRef(null);

  const ref = useCallback((el) => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    if (!el) return;
    const measure = () => setHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    cleanupRef.current = () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  return [ref, height];
}
